/**
 * Inline "create a missing repository" affordance for the repo pickers on
 * Settings ▸ Repository roles.
 *
 * Organization installations create through POST /github/repos/create —
 * repos the App creates join its installation automatically, so the parent
 * can append-and-select the returned repo without refetching. Personal
 * accounts get a github.com/new deep link instead (GitHub has no
 * server-to-server API to create repos on a user account); after creating
 * externally the repo must also be added to the App installation, hence
 * the refresh action.
 */

import { useState } from 'react';
import { Plus, ExternalLink, RefreshCw, AlertCircle } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import axios from 'axios';
import { githubApi, type Installation, type Repo } from '@/services/githubApi';

interface CreateRepoInlineProps {
  installations: Installation[];
  /** Prefilled repo name, e.g. "docs-requirements". */
  suggestedName: string;
  /** Called with the new repo so the parent can append it and select it. */
  onCreated: (repo: Repo) => void;
  /** Re-fetch the repo list (personal-account fallback path). */
  onRefresh?: () => void;
}

export default function CreateRepoInline({
  installations,
  suggestedName,
  onCreated,
  onRefresh,
}: CreateRepoInlineProps) {
  const orgs = installations.filter((i) => i.account_type === 'Organization');
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState('');
  const [name, setName] = useState(suggestedName);
  const [isPrivate, setIsPrivate] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (installations.length === 0) return null;

  const effectiveOwner = owner || orgs[0]?.account_login || '';

  async function handleCreate() {
    if (!name.trim() || !effectiveOwner) return;
    setCreating(true);
    setError(null);
    try {
      const repo = await githubApi.createRepo({
        owner: effectiveOwner,
        name: name.trim(),
        private: isPrivate,
      });
      onCreated(repo);
      setOpen(false);
      setName(suggestedName);
    } catch (e: unknown) {
      const data = axios.isAxiosError(e) ? e.response?.data : undefined;
      setError(data?.detail || data?.message || 'Failed to create the repository');
    } finally {
      setCreating(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
      >
        <Plus className="w-3.5 h-3.5" />
        Create a new repository…
      </button>
    );
  }

  // No org installation: the backend can't create on a personal account —
  // hand off to github.com/new and let the user pull the list again.
  if (orgs.length === 0) {
    const account = installations[0].account_login;
    const fallbackUrl = `https://github.com/new?name=${encodeURIComponent(name.trim() || suggestedName)}`;
    return (
      <div className="mt-2 max-w-md rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-3 text-sm text-slate-600 dark:text-slate-400 space-y-2">
        <p>
          SystemDox can only create repositories in <strong>organization</strong> installations,
          and <strong>{account}</strong> is a personal account. Create the repository on GitHub,
          add it to the SystemDox App installation, then refresh this list.
        </p>
        <div className="flex items-center gap-4">
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
          >
            Create on GitHub
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh list
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-slate-500 hover:underline"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 max-w-md rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-3 space-y-3">
      {error && (
        <div className="px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <select
          value={effectiveOwner}
          onChange={(e) => setOwner(e.target.value)}
          disabled={creating}
          aria-label="Organization"
          className="px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
        >
          {orgs.map((i) => (
            <option key={i.installation_id} value={i.account_login}>
              {i.account_login}
            </option>
          ))}
        </select>
        <span className="text-slate-400">/</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={creating}
          placeholder={suggestedName}
          aria-label="Repository name"
          className="flex-1 min-w-0 px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
        <input
          type="checkbox"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          disabled={creating}
          className="rounded border-slate-300 dark:border-slate-700"
        />
        Private repository
      </label>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {creating && <Spinner size="sm" tone="current" />}
          Create repository
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          disabled={creating}
          className="text-sm text-slate-500 hover:underline disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
